import React from "react";
import { Stack, Typography, Box } from "@mui/material";
import { useTranslations } from "next-intl";
import { CheckboxComponent, SecureDataComponent } from "@/components";
import { IMAGES, WEB_COLORS } from "@/lib/constants";
import ImageStepper from "@/components/ImageStepper";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";

const AadhaarInstructions = () => {
  const instructions = [
    {
      icon: <BadgeOutlinedIcon />,
      text: "Aadhaar number",
    },
    {
      icon: <PhoneOutlinedIcon />,
      text: "Aadhaar-linked mobile",
    },
    {
      icon: <TimerOutlinedIcon />,
      text: "Takes only a few minutes",
    },
  ];

  return (
    <Box
      sx={{
        width: "100%",
        boxSizing: "border-box",
        backgroundColor: "#EEF6F0",
        border: "1px solid #D0D9D4",
        borderRadius: "8px",
        px: { xs: 1, sm: 3, md: 4 },
        py: { xs: 1.5, sm: 2.5 },
        overflow: "hidden",
      }}>
      {/* Heading */}
      <Typography
        sx={{
          textAlign: "center",
          color: "#004D40",
          fontSize: {
            xs: "14px",
            sm: "17px",
            md: "18px",
          },
          fontWeight: 600,
          lineHeight: { xs: "20px", sm: "24px" },
          mb: {
            xs: 1.25,
            sm: 2.5,
          },
        }}>
        Keep These Ready
      </Typography>

      <Box
        sx={{
          display: "flex",
          width: "100%",
          flexDirection: "row",
        }}>
        {instructions.map((item, index) => (
          <React.Fragment key={item.text}>
            {index > 0 && (
              <Box
                sx={{
                  backgroundColor: "#D5DEDA",
                  width: "1px",
                  height: { xs: "64px", sm: "82px" },
                  alignSelf: "center",
                  display: "block",
                }}
              />
            )}

            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                minWidth: 0,
                py: {
                  xs: 0.5,
                  sm: 0,
                },
                px: { xs: 0.5, sm: 1 },
              }}>
              <Box
                sx={{
                  width: {
                    xs: 36,
                    sm: 52,
                  },
                  height: {
                    xs: 36,
                    sm: 52,
                  },
                  borderRadius: "50%",
                  backgroundColor: "#FFFFFF",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#00594F",
                  mb: {
                    xs: 0.75,
                    sm: 1.5,
                  },
                  "& svg": {
                    fontSize: {
                      xs: 20,
                      sm: 27,
                    },
                    strokeWidth: 1.5,
                  },
                }}>
                {item.icon}
              </Box>

              <Typography
                sx={{
                  color: "#202624",
                  fontSize: {
                    xs: "11px",
                    sm: "14px",
                    md: "15px",
                  },
                  fontWeight: 400,
                  lineHeight: { xs: "15px", sm: "20px" },
                  textAlign: "center",
                  overflowWrap: "anywhere",
                  whiteSpace: { xs: "normal", sm: "nowrap" },
                }}>
                {item.text}
              </Typography>
            </Box>
          </React.Fragment>
        ))}
      </Box>
    </Box>
  );
};

export default function EkycVerification({
  addressConsent,
  handleAddressConsent,
  handleKycConsent,
  kycConsent,
}) {
  const t = useTranslations("ekycVerification");

  const schema = [
    {
      image: IMAGES.consentAsk,
      label: t("steps.step1"),
    },
    {
      image: IMAGES.adharDetails,

      label: t("steps.step2"),
    },
    {
      image: IMAGES.enterOtp,
      label: t("steps.step3"),
    },
  ];

  return (
    <Stack spacing={5}>
      <Stack spacing={1} mb={2}>
        <Typography
          variant="h6"
          fontSize={{ xs: 16, sm: 18, md: 20, lg: 24 }}
          fontWeight={600}
          color={WEB_COLORS.darkTeal}>
          {t.rich("title1", {
            em: (chunks) => (
              <Typography
                component="span"
                fontWeight={600}
                fontSize={{ xs: 16, sm: 18, md: 20, lg: 24 }}
                color={WEB_COLORS.primaryLightShade2}>
                {chunks}
              </Typography>
            ),
          })}
        </Typography>
        <Typography
          color={"rgba(31, 31, 31, 0.8)"}
          fontSize={{ xs: 11, sm: 12, md: 14 }}
          mb={0.5}>
          {t("title2")}
        </Typography>
      </Stack>

      <Stack spacing={{ xs: 1, sm: 4 }}>
        <ImageStepper schema={schema} />
        <AadhaarInstructions />
      </Stack>

      <SecureDataComponent />

      {/* consent checkboxes */}
      <Stack spacing={2} mt={2}>
        <CheckboxComponent
          onChange={handleAddressConsent}
          name="addressConsent"
          checked={!!addressConsent}
          color={WEB_COLORS.errorColor}
          label={
            <Typography
              color={WEB_COLORS.textFadeColor}
              fontWeight={400}
              fontSize={{ xs: "12px", sm: "13px", md: "14px" }}
              sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              {t("consent1")}
            </Typography>
          }
        />
        <CheckboxComponent
          name="consent"
          onChange={handleKycConsent}
          checked={!!kycConsent}
          color={WEB_COLORS.errorColor}
          label={
            <Typography
              color={WEB_COLORS.textFadeColor}
              fontSize={{ xs: "12px", sm: "13px", md: "14px" }}
              sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              {t("consent2")}
            </Typography>
          }
        />
      </Stack>
    </Stack>
  );
}
